/*
 * @Author: Claude
 * @Date: 2025-12-31
 * @Description: 统一的媒体播放器组件（支持音频和视频）
 */
import { Progress, Slider } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

import styles from '../../AudioUpload/components/index.module.scss';
import { FILE_STATUS } from '../constants';
import { ExtendedUploadFile } from '../types';
import { formatTime, removeFileExtension } from '../utils';
import { isVideoFile } from '../utils/fileTypeDetector';

interface MediaPlayerItemProps {
  /** 文件对象 */
  file: ExtendedUploadFile;
  /** 删除回调 */
  onDelete: (e: React.MouseEvent) => void;
  /** 媒体类型（auto 自动检测，audio 音频，video 视频） */
  mediaType?: 'auto' | 'audio' | 'video';
}

/**
 * 统一的媒体播放器组件
 * 自动根据文件类型选择音频或视频播放
 * 使用统一的播放器 UI（横条布局 + 播放控制）
 */
export const MediaPlayerItem: React.FC<MediaPlayerItemProps> = ({
  file,
  onDelete,
  mediaType = 'auto',
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const mediaRef = useRef<HTMLAudioElement | HTMLVideoElement>(null);

  const isUploading = file.status === FILE_STATUS.UPLOADING;
  const uploadProgress = file.uploadProgress || 0;

  // 自动检测媒体类型
  const fileObj = file.originFileObj || (file as unknown as File);
  const detectedType =
    mediaType === 'auto' ? (isVideoFile(fileObj) ? 'video' : 'audio') : mediaType;

  // 加载媒体元数据
  useEffect(() => {
    const media = mediaRef.current;
    if (!media || isUploading) return;

    const handleLoadedMetadata = () => {
      setDuration(media.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(media.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    media.addEventListener('loadedmetadata', handleLoadedMetadata);
    media.addEventListener('timeupdate', handleTimeUpdate);
    media.addEventListener('ended', handleEnded);

    return () => {
      media.removeEventListener('loadedmetadata', handleLoadedMetadata);
      media.removeEventListener('timeupdate', handleTimeUpdate);
      media.removeEventListener('ended', handleEnded);
    };
  }, [file.url, isUploading]);

  // 播放/暂停控制
  const togglePlay = () => {
    const media = mediaRef.current;
    if (!media) return;

    if (isPlaying) {
      media.pause();
    } else {
      media.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Slider 拖动时更新播放位置
  const handleSliderChange = (value: number) => {
    const media = mediaRef.current;
    if (!media || isUploading) return;

    const newTime = (value / 100) * duration;
    media.currentTime = newTime;
    setCurrentTime(newTime);

    // 如果当前是播放状态，跳转后继续播放
    if (isPlaying) {
      media.play();
    }
  };

  // 计算播放进度百分比
  const playProgress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-3 p-4 bg-[#F4F5FE] rounded-lg">
      {/* 媒体元素 */}
      {!isUploading && file.url && (
        <>
          {detectedType === 'video' ? (
            <video
              ref={mediaRef as React.RefObject<HTMLVideoElement>}
              src={file.url}
              preload="metadata"
              style={{ display: 'none' }}
            />
          ) : (
            <audio
              ref={mediaRef as React.RefObject<HTMLAudioElement>}
              src={file.url}
              preload="metadata"
            />
          )}
        </>
      )}

      {/* 播放/暂停按钮 */}
      <button
        onClick={togglePlay}
        disabled={isUploading}
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          isUploading
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
        }`}
      >
        {isUploading ? (
          <span className="text-white text-xs">...</span>
        ) : isPlaying ? (
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 text-white ml-0.5"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {/* 文件信息和进度条 */}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-900 truncate">
            {removeFileExtension(file.name)}
          </span>
          <button
            onClick={onDelete}
            className="flex-shrink-0 ml-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* 进度条 */}
        {isUploading ? (
          <div className="flex gap-2">
            <Progress
              percent={uploadProgress}
              strokeColor="#3b82f6"
              trailColor="#e5e7eb"
              showInfo={false}
              size="small"
            />
            <div className="text-xs text-gray-500 w-[110px]">
              上传中 {uploadProgress}%
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 flex-shrink-0">
              {formatTime(currentTime)}
            </span>
            <Slider
              value={playProgress}
              onChange={handleSliderChange}
              tooltip={{ formatter: null }}
              style={{ margin: 0 }}
              styles={{
                track: { backgroundColor: '#4931FA' },
                rail: { backgroundColor: '#DFE3FD' },
              }}
              className={`${styles['custom-audio-slider']} flex-1`}
            />
            <span className="text-xs text-gray-500 flex-shrink-0">
              {formatTime(duration)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
